import { deltaE00, labToRgb } from './colour';
import { rgbToLab } from './palette';
import { NearestFinder } from './quantize';
import type { ColourMetric, PaletteEntry, Rgb } from './types';

export interface ToneMapOptions {
  /** Stretch or squeeze source lightness into the range the enabled blocks can actually build. */
  lightness: boolean;
  /** Pull colour the palette cannot reach back towards what it can, before dithering runs. */
  gamut: boolean;
}

export const NEUTRAL_TONE_MAP: ToneMapOptions = { lightness: false, gamut: false };

/**
 * Difference a pixel may keep from its nearest block. Below this the quantiser and the dither still
 * have real error to work with, which is what makes shading and texture happen; above it the error
 * is unreachable and only fed a bias into the diffusion.
 */
const KEEP_DE = 8;

/** Share of the unreachable part left in place, so distinct out of gamut colours stay distinct. */
const OVERSHOOT = 0.35;

export interface ToneMapReport {
  sourceMinL: number;
  sourceMaxL: number;
  paletteMinL: number;
  paletteMaxL: number;
  /** Share of opaque pixels that asked for more colour than the palette holds. */
  clamped: number;
}

/**
 * Brings the artwork into what the enabled blocks can build, before quantising. Without this a
 * saturated source leaves the same unreachable error on every pixel, and error diffusion spreads
 * that bias until whole flat areas land on a block nowhere near the colour asked for: a hot pink
 * fill comes out as magenta, a bright cyan as prismarine.
 */
export function toneMap(
  rgba: Uint8ClampedArray,
  entries: PaletteEntry[],
  options: ToneMapOptions,
  alphaThreshold: number,
  metric: ColourMetric = 'de2000',
): ToneMapReport | null {
  if (entries.length === 0) return null;
  if (!options.lightness && !options.gamut) return null;

  let paletteMinL = 100;
  let paletteMaxL = 0;
  for (const entry of entries) {
    paletteMinL = Math.min(paletteMinL, entry.lab[0]);
    paletteMaxL = Math.max(paletteMaxL, entry.lab[0]);
  }
  paletteMaxL = Math.max(paletteMaxL, paletteMinL + 1);

  const count = rgba.length / 4;
  const opaque = new Uint8Array(count);
  // Typed, not number[]: V8 caps a double array near 134 million entries, a 100 by 100 map build has 164 million.
  const lightnessBuffer = new Float32Array(count);
  let opaqueCount = 0;
  for (let p = 0; p < count; p++) {
    if (rgba[p * 4 + 3] < alphaThreshold) continue;
    opaque[p] = 1;
    lightnessBuffer[opaqueCount++] = rgbToLab([rgba[p * 4], rgba[p * 4 + 1], rgba[p * 4 + 2]])[0];
  }
  if (opaqueCount === 0) return null;

  const lightnesses = lightnessBuffer.subarray(0, opaqueCount).sort();
  // Percentiles, not the extremes: one stray highlight must not set the mapping for the artwork.
  const sourceMinL = lightnesses[Math.floor(lightnesses.length * 0.01)];
  const sourceMaxL = lightnesses[Math.floor(lightnesses.length * 0.99)];
  const sourceSpan = sourceMaxL - sourceMinL;
  const remapL = options.lightness && sourceSpan > 1;

  const finder = new NearestFinder(entries, metric);
  // The nearest block lookup is the expensive part and the correction it yields moves smoothly with
  // colour, so it is shared across a 5 bit per channel bucket. Caching the correction rather than
  // the result keeps every pixel's own value: a gradient stays a gradient instead of banding.
  const corrections = new Map<number, Rgb | null>();
  let clamped = 0;

  for (let p = 0; p < count; p++) {
    if (!opaque[p]) continue;

    let lab = rgbToLab([rgba[p * 4], rgba[p * 4 + 1], rgba[p * 4 + 2]]);
    if (remapL) {
      const share = Math.max(0, Math.min(1, (lab[0] - sourceMinL) / sourceSpan));
      lab = [paletteMinL + share * (paletteMaxL - paletteMinL), lab[1], lab[2]];
    }

    if (options.gamut) {
      const bucket = ((lab[0] / 100) * 31 + 0.5) | 0;
      const key =
        (bucket << 20) | ((((lab[1] + 128) / 256) * 31 + 0.5) << 10) | ((((lab[2] + 128) / 256) * 31 + 0.5) | 0);
      let correction = corrections.get(key);
      if (correction === undefined) {
        const rgb = labToRgb(lab);
        const nearest = entries[finder.closest(rgb[0], rgb[1], rgb[2])].lab;
        const distance = deltaE00(lab, nearest);
        if (distance > KEEP_DE) {
          const allowed = KEEP_DE + (distance - KEEP_DE) * OVERSHOOT;
          const factor = allowed / distance;
          correction = [
            (nearest[0] - lab[0]) * (1 - factor),
            (nearest[1] - lab[1]) * (1 - factor),
            (nearest[2] - lab[2]) * (1 - factor),
          ];
        } else {
          correction = null;
        }
        corrections.set(key, correction);
      }
      if (correction) {
        clamped++;
        lab = [lab[0] + correction[0], lab[1] + correction[1], lab[2] + correction[2]];
      }
    }

    const mapped: Rgb = labToRgb(lab);
    rgba[p * 4] = Math.round(mapped[0]);
    rgba[p * 4 + 1] = Math.round(mapped[1]);
    rgba[p * 4 + 2] = Math.round(mapped[2]);
  }

  return {
    sourceMinL,
    sourceMaxL,
    paletteMinL,
    paletteMaxL,
    clamped: clamped / lightnesses.length,
  };
}
